import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const StepPropertyBasics = ({ data, onNext, onBack }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    type: data?.type || 'house',
    listingType: data?.listingType || 'buy',
    beds: data?.beds || 3,
    baths: data?.baths || 2,
    parking: data?.parking || 2
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    onNext(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{t('propertyDetailsTitle')}</h2>
        <p className="text-muted-foreground">{t('propertyDetailsDesc')}</p>
      </div>
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">{t('propertyType')}</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { value: 'house', label: t('house'), icon: 'Home' },
              { value: 'unit', label: t('unitApartment'), icon: 'Building' },
              { value: 'commercial', label: t('commercialLabel'), icon: 'Building2' }
            ]?.map((type) => (
              <button
                key={type?.value}
                type="button"
                onClick={() => setFormData({ ...formData, type: type?.value })}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData?.type === type?.value
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <Icon name={type?.icon} size={24} color="var(--color-primary)" className="mx-auto mb-2" />
                <p className="font-semibold text-foreground text-center">{type?.label}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">{t('listingType')}</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { value: 'buy', label: t('forSaleLabel') },
              { value: 'rent', label: t('forRentLabel') },
              { value: 'commercial', label: t('commercialLabel') },
              { value: 'off-market', label: t('offMarketLabel') }
            ]?.map((type) => (
              <button
                key={type?.value}
                type="button"
                onClick={() => setFormData({ ...formData, listingType: type?.value })}
                className={`p-3 rounded-lg border-2 transition-all ${
                  formData?.listingType === type?.value
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <p className="font-semibold text-foreground text-center text-sm">{type?.label}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t('bedrooms')}</label>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="icon" onClick={() => setFormData({ ...formData, beds: Math.max(0, formData?.beds - 1) })}>
                <Icon name="Minus" size={16} />
              </Button>
              <Input type="number" value={formData?.beds} onChange={(e) => setFormData({ ...formData, beds: parseInt(e?.target?.value) || 0 })} className="text-center" min="0" />
              <Button type="button" variant="outline" size="icon" onClick={() => setFormData({ ...formData, beds: formData?.beds + 1 })}>
                <Icon name="Plus" size={16} />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t('bathrooms')}</label>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="icon" onClick={() => setFormData({ ...formData, baths: Math.max(0, formData?.baths - 1) })}>
                <Icon name="Minus" size={16} />
              </Button>
              <Input type="number" value={formData?.baths} onChange={(e) => setFormData({ ...formData, baths: parseInt(e?.target?.value) || 0 })} className="text-center" min="0" />
              <Button type="button" variant="outline" size="icon" onClick={() => setFormData({ ...formData, baths: formData?.baths + 1 })}>
                <Icon name="Plus" size={16} />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t('parkingSpaces')}</label>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="icon" onClick={() => setFormData({ ...formData, parking: Math.max(0, formData?.parking - 1) })}>
                <Icon name="Minus" size={16} />
              </Button>
              <Input type="number" value={formData?.parking} onChange={(e) => setFormData({ ...formData, parking: parseInt(e?.target?.value) || 0 })} className="text-center" min="0" />
              <Button type="button" variant="outline" size="icon" onClick={() => setFormData({ ...formData, parking: formData?.parking + 1 })}>
                <Icon name="Plus" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack} iconName="ArrowLeft">{t('back')}</Button>
        <Button type="submit" variant="default" iconName="ArrowRight" iconPosition="right">{t('continue')}</Button>
      </div>
    </form>
  );
};

export default StepPropertyBasics;